import { ApplicationInsights, IEventTelemetry, SeverityLevel } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import * as H from 'history';

let reactPlugin: ReactPlugin;
let appInsights: ApplicationInsights;

/**
 * Create the App Insights Telemetry Service
 * @return {{reactPlugin: ReactPlugin, appInsights: Object, initialize: Function}} - Object
 */
const createTelemetryService = (): { reactPlugin: ReactPlugin; appInsights: ApplicationInsights; initialize: (instrumentationKey: string, browserHistory: H.History<unknown>) => void } => {
  /**
   * Initialize the Application Insights class
   * @param {string} instrumentationKey - Application Insights Instrumentation Key
   * @param {Object} browserHistory - client's browser history, supplied by the withRouter HOC
   * @return {void}
   */
  const initialize = (instrumentationKey: string, browserHistory: H.History<unknown>): void => {
    if (!browserHistory) {
      throw new Error('Could not initialize Telemetry Service');
    }
    if (!instrumentationKey) {
      throw new Error('Instrumentation key not provided in ./src/telemetry-provider.jsx');
    }

    reactPlugin = new ReactPlugin();

    appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: instrumentationKey,
        maxBatchInterval: 0,
        disableFetchTracking: false,
        extensions: [reactPlugin],
        extensionConfig: {
          [reactPlugin.identifier]: {
            history: browserHistory,
          },
        },
      },
    });

    appInsights.loadAppInsights();
    // Dev Note: Microsoft says we should do this: https://docs.microsoft.com/en-us/azure/azure-monitor/app/javascript#npm-based-setup
    appInsights.trackPageView();
  };

  return { reactPlugin, appInsights, initialize };
};

export function addOtherDefaultTrackingProperties(customProperties: {
  clientId: number | null;
  stateId: number | null;
  districtId: number | null;
  schoolId: number | null;
  clientName: string | null;
  stateName: string | null;
  districtName: string | null;
  schoolName: string | null;
  userId: number | null;
  gradeId: number | null;
}): Promise<void> {
  appInsights.addTelemetryInitializer(function(envelope) {
    const telemetryItem = envelope.baseData;

    if (telemetryItem != null) {
      telemetryItem.properties = telemetryItem.properties || {};
      telemetryItem.properties = Object.assign(telemetryItem.properties, customProperties);
    }
  });

  return Promise.resolve();
}

export const ai = createTelemetryService();
export const getAppInsights = (): ApplicationInsights => appInsights;
export const Track = {
  Exception(error: Error): void {
    appInsights.trackException({ error: error, severityLevel: SeverityLevel.Error });
  },
  Trace(message: string): void {
    appInsights.trackTrace({ message: message, severityLevel: SeverityLevel.Information });
  },
  Event(event: IEventTelemetry): void {
    appInsights.trackEvent(event);
  },
  SpaceStation(checkpointName: string, checkpointId: number, stepId: number): void {
    appInsights.trackEvent({ name: 'SpaceStation', properties: { checkpointName: checkpointName, checkpointId: checkpointId, stepId: stepId } });
  },
  Planet(planetName: string, planetId: number, checkpointName: string, checkpointId: number, stepId: number): void {
    appInsights.trackEvent({ name: 'Planet', properties: { planetName: planetName, checkpointName: checkpointName, planetId: planetId, checkpointId: checkpointId, stepId: stepId } });
  },
  Activity(type: 'QUIZ' | 'SURVEY', activityName: string, activityId: number, activityPart: 'INTRO' | 'CONCLUSION' | 'QUESTION' | 'SUMMARY', stepNumber: number): void {
    appInsights.trackEvent({ name: 'Activity', properties: { type: type, activityName: activityName, activityId: activityId, activityPart: activityPart, stepNumber: stepNumber } });
  },
  PlayGame(gameName: string, gameId: number, planetName: string, planetId: number): void {
    appInsights.trackEvent({ name: 'PlayGame', properties: { gameName: gameName, gameId: gameId, planetName: planetName, planetId: planetId } });
  },
  CompleteGame(gameName: string, gameId: number, planetName: string, planetId: number): void {
    appInsights.trackEvent({ name: 'CompleteGame', properties: { gameName: gameName, gameId: gameId, planetName: planetName, planetId: planetId } });
  },
  BrowseVideo(name: string, id: number): void {
    appInsights.trackEvent({ name: 'BrowseVideo', properties: { videoName: name, videoId: id } });
  },
  CompletePlanet(name: string, id: number): void {
    appInsights.trackEvent({ name: 'CompletePlanet', properties: { planetName: name, planetId: id } });
  },
  SkipGame(gameName: string, gameId: number, planetName: string, planetId: number): void {
    appInsights.trackEvent({ name: 'SkipGame', properties: { gameName: gameName, gameId: gameId, planetName: planetName, planetId: planetId } });
  },
  OpenIntercom(userId: number): void {
    appInsights.trackEvent({ name: 'OpenIntercom', properties: { userId } });
  },
};



// WEBPACK FOOTER //
// ./src/providers/TelemetryService.ts